import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import { createSnackButtons } from 'utils/helpers/function';
import { useSnackbar } from 'notistack';

/** How to test on local
 * Pre-requisite: Make sure serve is installed with `npm install -g serve`
 *
 * 1. Change the version in the package.json with `npm version [version number] --no-git-tag-version`
 * 2. Run `npm run build:production-local`
 * 3. Run `serve -s build`
 * 4. Open the browser and go to `http://localhost:3000`
 * 5. Open the console and check the logs
 * 6. Change the version in the package.json
 * 7. To test first visit, close the browser
 * 8. Run `npm run build:production-local`
 * 9. Open the browser and go to `http://localhost:3000` again
 */

// eslint-disable-next-line import/prefer-default-export
export const useCheckForUpdates = () => {
  const { closeSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const dispatchUpdateNotification = useCallback(() => {
    dispatch(
      createSnack({
        message: 'A new version of Swift is available.',
        options: {
          action: createSnackButtons([
            {
              label: 'Update Now',
              onClick: () => {
                if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
                  navigator.serviceWorker.ready.then(registration => {
                    // Force the service worker to activate immediately
                    registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
                  });
                }
                window.location.reload(true);
              },
            },
            {
              label: 'CLOSE',
              onClick: () => {
                closeSnackbar('new-version');
              },
            },
          ]),
          autoHideDuration: 60000,
          key: 'new-version',
          sx: {
            '& .SnackbarContent-root.SnackbarItem-contentRoot': {
              '& .SnackbarItem-action .MuiBox-root .MuiButton-root': {
                color: 'text.primary',
              },
              backgroundColor: 'white',
              backgroundImage: 'url(/icons/logo-72.png)',
              backgroundPosition: '.5rem',
              backgroundRepeat: 'no-repeat',
              borderRight: '10px solid #267E4D',
              color: 'text.primary',
              fontSize: '1rem',
              fontWeight: 'bold',
              padding: '2rem 2rem 2rem 5rem',
            },
          },
        },
      })
    );
  }, [closeSnackbar, dispatch]);

  useEffect(() => {
    // Check for updates immediately when component mounts
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        window.dispatchEvent(
          new CustomEvent('checkingUpdate', { detail: new Date().toISOString() })
        );
        registration.update().then(() => {
          window.dispatchEvent(
            new CustomEvent('updateAttempted', { detail: new Date().toLocaleTimeString() })
          );
        });
      });
    }

    // Listen for the custom event from service worker
    window.addEventListener('serviceWorkerUpdate', () => {
      dispatchUpdateNotification();
    });

    // Check for updates every 10 seconds
    const interval = setInterval(() => {
      if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
        window.dispatchEvent(
          new CustomEvent('checkingUpdate', { detail: new Date().toISOString() })
        );

        navigator.serviceWorker.ready.then(registration => {
          registration.update().then(() => {
            window.dispatchEvent(
              new CustomEvent('updateAttempted', { detail: new Date().toLocaleTimeString() })
            );
          });
        });
      }
    }, process.env.REACT_APP_UPDATE_INTERVAL || 300000);

    // window.addEventListener('checkingUpdate', e => {
    //   // eslint-disable-next-line no-console
    //   console.log('Service Worker: Checking for update at:', e.detail);
    //   // For testing in production, you could use:
    //   // document.title = `Checking update: ${new Date().toLocaleTimeString()}`;
    // });

    // window.addEventListener('updateAttempted', e => {
    //   // eslint-disable-next-line no-console
    //   console.log('Service Worker: Update attempted at:', e.detail);
    //   // For testing in production, you could use:
    //   // document.title = `Update attempted: ${new Date().toLocaleTimeString()}`;
    // });

    return () => clearInterval(interval);
  }, [dispatchUpdateNotification]);
};
